import type { SessionTrailPoint } from '@repo/types/sessions/session-types';
import { easeInOutCubic } from './animation-utils';

/**
 * Interpolates between coordinates based on progress (0-1)
 * @param coordinates Array of [lng, lat] coordinate pairs
 * @param progress Value between 0 and 1 representing position along the path
 * @param options Optional settings including whether to apply easing
 * @returns Interpolated coordinate or null if invalid
 */
export function interpolateCoordinates(coordinates: [number, number][], progress: number): [number, number] | null {
    if (!coordinates.length) return null;
    if (progress <= 0) return coordinates[0] ?? null;
    if (progress >= 1) return coordinates[coordinates.length - 1] ?? null;

    const totalSegments = coordinates.length - 1;
    const targetIndex = progress * totalSegments;
    const lowerIndex = Math.floor(targetIndex);
    const upperIndex = Math.min(lowerIndex + 1, totalSegments);
    let t = targetIndex - lowerIndex;

    t = easeInOutCubic(t);

    const lowerPoint = coordinates[lowerIndex];
    const upperPoint = coordinates[upperIndex];

    if (!lowerPoint || !upperPoint) return lowerPoint ?? upperPoint ?? null;

    return [lowerPoint[0] + (upperPoint[0] - lowerPoint[0]) * t, lowerPoint[1] + (upperPoint[1] - lowerPoint[1]) * t];
}

/**
 * Interpolates between trail points based on progress (0-1)
 * @param trail Array of session trail points
 * @param progress Value between 0 and 1 representing position along the path
 * @param options Optional settings including whether to apply easing
 * @returns Interpolated trail point or null if invalid
 */
export function interpolateTrailPoint(trail: SessionTrailPoint[], progress: number): SessionTrailPoint | null {
    if (!trail.length) return null;

    const firstPoint = trail[0];
    if (progress <= 0) return firstPoint ?? null;

    const lastPoint = trail[trail.length - 1];
    if (progress >= 1) return lastPoint ?? null;

    const totalPoints = trail.length - 1;
    const targetIndex = progress * totalPoints;
    const lowerIndex = Math.floor(targetIndex);
    const upperIndex = Math.min(lowerIndex + 1, totalPoints);
    let t = targetIndex - lowerIndex;

    t = easeInOutCubic(t);

    const lowerPoint = trail[lowerIndex];
    const upperPoint = trail[upperIndex];

    if (!lowerPoint || !upperPoint) return lowerPoint ?? upperPoint ?? null;

    return {
        x: lowerPoint.x + (upperPoint.x - lowerPoint.x) * t,
        y: lowerPoint.y + (upperPoint.y - lowerPoint.y) * t,
        z: lowerPoint.z + (upperPoint.z - lowerPoint.z) * t,
        timestamp: lowerPoint.timestamp + (upperPoint.timestamp - lowerPoint.timestamp) * t,
    };
}
